import { createApiWithNewBasePath } from "@api";
import { getCurrentCountry } from "@config";
import { USE_MOCK_CATEGORY_TREE } from "../config/constants";
import bricolageCategories from "../fixtures/bricolageCategories.json";

/**
 * Category tree for the task-creation flow (RT-4321).
 *
 * A logged-out visitor needs the Bricolage p_category's identity and its
 * s_categories. `API::CategoriesController` sits behind `PrivateController`
 * and 401s for a guest, but `API::Public::CategoriesController` (mounted at
 * `/api/public/categories`) serves the same payload without auth — that is
 * what `fetchCategoryTree` calls below. `USE_MOCK_CATEGORY_TREE`
 * (`config/constants.ts`) still exists for local/test use; the draft and
 * config endpoints are live and mock nothing.
 *
 * `fixtures/bricolageCategories.json` mirrors the real shape
 * (`{ categories: [CategoryThemeResource] }`, each theme's `categories`
 * holding `CategoryResource` entries — see
 * `components/domain/app/resources/domain/category_theme_resource.rb` and
 * `category_resource.rb`) for one theme: Bricolage (`PCategory::DIY = 4`,
 * `app/models/p_category.rb:28`).
 *
 * Provenance for each s_category id — cross-checked against the `DIY_*`
 * constants in `app/models/s_category.rb` and this flow's 8 config fixtures
 * (`fixtures/*.json`, each carrying `category.key` / `category.s_category_id`):
 *
 *   id  | keyName            | source
 *   ----|--------------------|----------------------------------------------
 *   115 | painter            | s_category.rb DIY_PEINTRE + fixtures/painter.json
 *   117 | electrician        | s_category.rb DIY_ELECTRICIEN + fixtures/electrician.json
 *   118 | carpenter          | s_category.rb DIY_MENUISIER + fixtures/carpenter.json
 *   119 | appliance_repairer | s_category.rb DIY_REPARATEUR_ELECTRO + fixtures/appliance_repairer.json
 *   120 | plumber            | s_category.rb DIY_PLOMBIER + fixtures/plumber.json
 *   121 | handyman           | fixtures/handyman.json only — DISCREPANCY: s_category.rb's
 *       |                    | id 121 is `DIY_ALL`, not a handyman-specific constant.
 *   133 | lock_repairer      | fixtures/lock_repairer.json only — no `DIY_*` constant
 *       |                    | exists for 133 in s_category.rb.
 *   134 | bicycle_repairer   | fixtures/bicycle_repairer.json only — no `DIY_*` constant
 *       |                    | exists for 134 in s_category.rb.
 *
 * Fixture ids win over the constants where they disagree, per instruction:
 * the 8 fixtures are the categories this flow actually supports, and no id
 * here was invented — every one traces to at least one of the two sources
 * above.
 */

export interface ICategoryNode {
  id: number;
  keyName: string;
  categories?: ICategoryNode[];
}

interface ICategoryTreeResponse {
  categories: ICategoryNode[];
}

/** Query params the tree endpoint takes. `country` is an ISO 3166-1 alpha-2. */
interface ICategoryTreeParams {
  country: string;
}

/**
 * `country` is the request's own (`getCurrentCountry`), not a fixed one: the
 * tree is filtered per country server-side
 * (`Domain::Categories::FindAll#country_exists?` looks the param up as
 * `iso_code`), so a Belgian visitor sent `fr` gets France's categories.
 */
export const fetchCategoryTree = async (
  country: string = getCurrentCountry()
): Promise<ICategoryNode[]> => {
  if (USE_MOCK_CATEGORY_TREE) {
    return (bricolageCategories as ICategoryTreeResponse).categories;
  }

  // The unauthenticated twin of `API::CategoriesController` — that one is
  // marked deprecated in its own class comment specifically for callers like
  // this to move off of it (`app/controllers/api/categories_controller.rb`).
  // Same serializer, same payload shape, just no `current_user` in the query.
  //
  // Params go flat as the second argument: `createApi.get` wraps them in
  // axios's `{ params }` itself, so a wrapper here would serialise as
  // `?params[country]=be`.
  const { data } = await createApiWithNewBasePath.get<
    ICategoryTreeParams,
    ICategoryTreeResponse
  >("public/categories", { country });

  return data.categories;
};

/** Depth-first lookup by id. Never throws — returns `undefined` when absent. */
export const findPCategory = (
  tree: ICategoryNode[],
  pCategoryId: number
): ICategoryNode | undefined => tree.find((node) => node.id === pCategoryId);

/** `[]` for an unknown p_category id, never throws. */
export const findSCategories = (
  tree: ICategoryNode[],
  pCategoryId: number
): ICategoryNode[] => findPCategory(tree, pCategoryId)?.categories ?? [];

/**
 * Depth-first lookup of one s_category by id, across every theme — the SEM
 * pages send `s_category` without an accompanying theme, so this cannot
 * narrow by p_category first.
 */
export const findSCategory = (
  tree: ICategoryNode[],
  sCategoryId: number
): ICategoryNode | undefined => {
  for (const theme of tree) {
    const match = theme.categories?.find((node) => node.id === sCategoryId);

    if (match) return match;
  }

  return undefined;
};

/**
 * The theme an s_category belongs to (RT-4321).
 *
 * The SEM pages send `s_category` with no theme beside it
 * (`app/helpers/shared_helper.rb`: the two params are mutually exclusive), and
 * a draft carries no theme of its own until a subcategory is picked, so this
 * is what resolves one from the id that *is* known. The same walk
 * `findSCategory` does, keeping the theme it finds the match in rather than
 * throwing it away.
 */
export const findPCategoryBySCategory = (
  tree: ICategoryNode[],
  sCategoryId: number
): ICategoryNode | undefined =>
  tree.find((theme) =>
    theme.categories?.some((node) => node.id === sCategoryId)
  );

export interface ICategorySelection {
  pCategoryId?: number;
  sCategoryId?: number;
}

/**
 * The `keyName` behind a `taskCreation.category.{key}` message, from whichever
 * of the two ids the URL carries.
 *
 * The s_category wins when both are present: it is the specific one, and it is
 * what the copy wants ("top plumbers" reads better than "top DIY pros").
 * `undefined` when neither id is given or neither resolves — callers decide
 * what to show instead, so this never invents a key.
 */
export const resolveCategoryKey = (
  tree: ICategoryNode[],
  { pCategoryId, sCategoryId }: ICategorySelection
): string | undefined => {
  const node =
    (sCategoryId !== undefined
      ? findSCategory(tree, sCategoryId)
      : undefined) ??
    (pCategoryId !== undefined ? findPCategory(tree, pCategoryId) : undefined);

  return node?.keyName;
};
